import { FC, FocusEvent, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { Link } from 'react-router-dom'
import { SuccessModal, TwoFactorVerificationModal } from 'src/components/elements/modals'
import { Button, InputField } from 'src/components/ui'
import { api, useLoginMutation } from 'src/redux/api'
import { useAppDispatch } from 'src/redux/hooks'
import { parsePhone } from 'src/utils/phone'
import { AsYouType } from 'libphonenumber-js/max'

interface ILoginForm {}

const emailPattern = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/

const LoginForm: FC<ILoginForm> = () => {
  const { t } = useTranslation()

  const [login, { isLoading }] = useLoginMutation()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  // Guests bounced off a protected page carry it in location.state — return
  // there after login instead of always landing on home.
  const afterLoginPath = (location.state as { from?: string } | null)?.from || '/'

  // Social login round-trips through the backend, so router state is lost —
  // stash the destination for AuthSuccess to pick up after the OAuth redirect.
  useEffect(() => {
    if (afterLoginPath !== '/') {
      sessionStorage.setItem('auth_redirect', afterLoginPath)
    }
  }, [afterLoginPath])
  const [openSuccessModal, setOpenSuccessModal] = useState(false)
  const [show2FAModal, setShow2FAModal] = useState(false)
  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      emailOrPhone: '',
      password: '',
    },
  })

  const emailOrPhone = watch('emailOrPhone')

  const loginAwait = async (data: any) => {
    try {
      const parsed = parsePhone(data.emailOrPhone)
      const payload = {
        ...data,
        emailOrPhone: parsed?.e164 ?? data.emailOrPhone,
      }
      const response = await login(payload).unwrap()

      // Check if 2FA is required
      if (response.requires2FA) {
        setShow2FAModal(true)
        return
      }

      // Regular login success
      dispatch(api.util.invalidateTags(['User']))
      navigate(afterLoginPath)
    } catch (error: any) {
      const code = error?.data?.code
      let message: string
      if (code === 'ACCOUNT_NOT_VERIFIED') {
        message = t('login.account_not_verified')
      } else if (code === 'INVALID_CREDENTIALS') {
        message = t('login.wrong_email_or_password')
      } else {
        message = error?.data?.message || t('login.wrong_email_or_password')
      }
      setError('root', { message })
    }
  }

  const handle2FAVerified = () => {
    setShow2FAModal(false)
    dispatch(api.util.invalidateTags(['User']))
    navigate(afterLoginPath)
  }

  const onSubmit = async (data: any) => {
    await loginAwait(data)
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 self-stretch w-full">
        {errors.root?.message && (
          <p className="m-0 px-3.5 py-2.5 rounded-xl bg-danger-soft text-sm font-bold text-danger">
            {errors.root?.message}
          </p>
        )}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col">
            <Controller
              name="emailOrPhone"
              control={control}
              rules={{
                required: t('login.requiredField'),
                validate: (value) => {
                  const stripped = value.replaceAll(' ', '')
                  if (!emailPattern.test(stripped) && parsePhone(stripped) === null) {
                    return t('login.emailOrPhoneError')
                  }
                  return true
                },
              }}
              render={({ field }) => (
                <InputField
                  {...field}
                  onBlur={(e: FocusEvent<HTMLInputElement>) => {
                    const value = e.target.value
                    const parsed = parsePhone(value)
                    if (parsed) {
                      const formatter = new AsYouType(parsed.country as 'UA')
                      const formatted = formatter.input(parsed.e164) || parsed.e164
                      field.onChange(formatted)
                    }
                    field.onBlur()
                  }}
                  label={t('login.emailOrPhone')}
                  autoComplete="username"
                  error={errors.emailOrPhone}
                />
              )}
            />
            {(() => {
              const parsed = parsePhone(emailOrPhone)
              if (parsed && parsed.isMobile) {
                return <span className="text-xs text-ink-2 mt-1.5">→ {parsed.e164}</span>
              }
              if (parsed && !parsed.isMobile) {
                return (
                  <span className="text-xs font-bold text-warning mt-1.5">
                    → {parsed.e164} {t('login.phone_no_sms_hint')}
                  </span>
                )
              }
              return null
            })()}
          </div>
          <Controller
            name="password"
            control={control}
            rules={{
              required: t('login.requiredField'),
              minLength: { value: 6, message: t('login.passwordMinLengthError') },
            }}
            render={({ field }) => (
              <InputField
                {...field}
                label={t('login.password')}
                type="password"
                autoComplete="current-password"
                error={errors.password}
              />
            )}
          />
          <Link
            to="/forgot-password"
            className="self-end -mt-2 min-h-11 inline-flex items-center text-sm font-bold text-brand-ink hover:underline"
          >
            {t('login.forgot_password')}
          </Link>
        </div>
        <Button size="lg" className="w-full" type="submit" isLoading={isLoading}>
          {t('login.submitButton')}
        </Button>
      </form>
      <SuccessModal
        title={t('login.successModal.title')}
        text={t('login.successModal.text')}
        buttonText={t('login.successModal.closeButton')}
        open={openSuccessModal}
        handleClose={() => setOpenSuccessModal(false)}
      />
      <TwoFactorVerificationModal
        open={show2FAModal}
        handleClose={() => setShow2FAModal(false)}
        onVerified={handle2FAVerified}
      />
    </>
  )
}

export default LoginForm
